/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Select, Loading, Datepicker, HelpIcon} from '../../../components'
import {PageTitle} from '../../../core'
import {useDispatch, useSelector} from 'react-redux'
import {userTaskCreate} from '../../../redux/slices/usertask.slice'
import {allUserLookup, allProjectLookup, basicLookup} from '../../../redux/slices/lookup.slice'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs,
} from '../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  allUserLookupList: state.lookup.allUserLookupList,
  allProjectLookupList: state.lookup.allProjectLookupList,
  TaskPriority: state.lookup.TaskPriority,
  UserTaskAuthorizationType: state.lookup.UserTaskAuthorizationType,
  loading: state.lookup.loading,
})

const selectUserTaskData = (state) => ({
  isRedirect: state.userTask.isRedirect,
  loading: state.userTask.loading,
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)
  const {allUserLookupList, allProjectLookupList, TaskPriority, UserTaskAuthorizationType, loading} =
    useSelector(selectLookupData)
  const {isRedirect, loading: createLoading} = useSelector(selectUserTaskData)
  const dispatch = useDispatch()

  const validSchema = useMemoizedValidationSchema(
    () =>
      Yup.object().shape({
        Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        UserTaskAuthorizationTypeId: Yup.string().required(
          intl.formatMessage({id: 'REQUIRED.GENERAL'})
        ),
        UserIds: Yup.array()
          .of(Yup.string())
          .when('UserTaskAuthorizationTypeId', {
            is: (val) => Number(val) === 1,
            then: (schema) =>
              schema
                .min(1, intl.formatMessage({id: 'REQUIRED.GENERAL'}))
                .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
            otherwise: (schema) => schema.nullable(),
          }),
        ProjectId: Yup.string()
          .nullable()
          .when('UserTaskAuthorizationTypeId', {
            is: (val) => Number(val) === 2,
            then: (schema) => schema.required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
            otherwise: (schema) => schema.nullable(),
          }),
        TaskDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaskPriorityId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      }),
    [intl]
  )

  useEffect(() => {
    dispatch(allUserLookup())
    dispatch(allProjectLookup())
    dispatch(basicLookup('TaskPriority'))
    dispatch(basicLookup('UserTaskAuthorizationType'))
  }, [dispatch])

  const formValues = useMemoizedFormValues({
    Name: '',
    UserTaskAuthorizationTypeId: '',
    UserIds: [],
    ProjectId: null,
    TaskDate: new Date(),
    TaskPriorityId: '',
  })

  const handleSubmit = useCallback(
    async (values, {setSubmitting}) => {
      setSubmitting(true)
      const result = await dispatch(userTaskCreate(values))
      if (userTaskCreate.rejected.match(result)) {
        setSubmitting(false)
      }
    },
    [dispatch]
  )

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const breadcrumbs = useMemoizedBreadcrumbs(
    () => [
      {
        title: intl.formatMessage({id: 'BC.USERTASK'}),
        path: '/user/task',
        isSeparator: false,
        isActive: false,
      },
      {
        title: '',
        path: '',
        isSeparator: true,
        isActive: false,
      },
    ],
    [intl]
  )

  useEffect(() => {
    if (isRedirect && isRedirect.id) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
    }
  }, [isRedirect, navigate, pageUrl])

  const authTypeId = Number(formik.values.UserTaskAuthorizationTypeId)

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.USERTASK.ADD'})}</PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardHeader title={intl.formatMessage({id: 'BC.USERTASK.ADD'})}>
              <HelpIcon articleId='user-task-create' />
            </KTCardHeader>
            <KTCardBody>
              <form
                className='form'
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                    e.preventDefault()
                  }
                }}
                onSubmit={formik.handleSubmit}
                noValidate
              >
                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USERTASK.CREATE.NAME'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <input
                      type='text'
                      required
                      className='form-control form-control-solid mb-3 mb-lg-0'
                      autoComplete='off'
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps('Name')}
                    />
                    {formik.touched.Name && formik.errors.Name && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.Name}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USERTASK.CREATE.AUTHORIZATIONTYPE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={UserTaskAuthorizationType}
                      required
                      {...formik.getFieldProps('UserTaskAuthorizationTypeId')}
                      onChange={(item) => {
                        formik.setFieldValue('UserTaskAuthorizationTypeId', item.value)
                        formik.setFieldValue('UserIds', [])
                        formik.setFieldValue('ProjectId', null)
                      }}
                    />
                    {formik.touched.UserTaskAuthorizationTypeId &&
                      formik.errors.UserTaskAuthorizationTypeId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.UserTaskAuthorizationTypeId}</span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                {authTypeId === 1 && (
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'USERTASK.CREATE.USERID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={allUserLookupList}
                        required
                        {...formik.getFieldProps('UserIds')}
                        isMulti
                        closeMenuOnSelect={false}
                        onChange={(item) => {
                          const newData = []
                          item?.map((data) => newData.push(data.value))
                          formik.setFieldValue('UserIds', newData)
                        }}
                      />
                      {formik.touched.UserIds && formik.errors.UserIds && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.UserIds}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {authTypeId === 2 && (
                  <div className='row mb-6'>
                    <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                      {intl.formatMessage({id: 'USERTASK.CREATE.PROJECTID'})}
                    </label>
                    <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                      <Select
                        data={allProjectLookupList}
                        required
                        {...formik.getFieldProps('ProjectId')}
                        onChange={(item) => formik.setFieldValue('ProjectId', item.value)}
                      />
                      {formik.touched.ProjectId && formik.errors.ProjectId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.ProjectId}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USERTASK.CREATE.TASKDATE'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Datepicker
                      {...formik.getFieldProps('TaskDate')}
                      onChange={([date]) => formik.setFieldValue('TaskDate', date)}
                    />
                  </div>
                </div>

                <div className='row mb-6'>
                  <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                    {intl.formatMessage({id: 'USERTASK.CREATE.TASKPRIORITYID'})}
                  </label>
                  <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                    <Select
                      data={TaskPriority}
                      {...formik.getFieldProps('TaskPriorityId')}
                      onChange={(item) => formik.setFieldValue('TaskPriorityId', item.value)}
                    />
                    {formik.touched.TaskPriorityId && formik.errors.TaskPriorityId && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.TaskPriorityId}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='row pt-15'>
                  <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                    <button
                      type='button'
                      className='btn btn-light'
                      onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}`)}
                    >
                      {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                    </button>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                    >
                      <span className='indicator-label'>
                        {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                      </span>
                      {formik.isSubmitting && (
                        <span className='indicator-progress'>
                          {intl.formatMessage({id: 'PLEASE'})}
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {(loading || createLoading) && <Loading />}
    </>
  )
}
export {Create}
